export const INNOVATOR_FOUNDER = {
  slug: "innovator-founder",
  title: "UK Innovator Founder",
  eyebrow: "Founder programme",
  summary:
    "A structured initial assessment for founders exploring the UK Innovator Founder route. The assessment screens readiness for senior review; it does not decide visa eligibility or guarantee endorsement.",
  reviewed: "28 August 2026",
  sources: [
    { label: "GOV.UK — Innovator Founder overview", href: "https://www.gov.uk/innovator-founder-visa" },
    { label: "GOV.UK — Eligibility", href: "https://www.gov.uk/innovator-founder-visa/eligibility" },
    { label: "GOV.UK — Immigration Rules Appendix Innovator Founder", href: "https://www.gov.uk/guidance/immigration-rules/immigration-rules-appendix-innovator-founder" },
    { label: "GOV.UK — Authorised endorsing bodies", href: "https://www.gov.uk/government/publications/endorsing-bodies-innovator-founder-and-scale-up-visas" },
  ],
  questions: [
    {
      id: "age18",
      group: "Founder basics",
      prompt: "Are you at least 18 years old?",
      help: "The route requires applicants to be at least 18.",
      core: true,
    },
    {
      id: "englishB2",
      group: "Founder basics",
      prompt: "Can you currently evidence English at CEFR B2 or above, or do you expect to meet an accepted exemption?",
      help: "The route has a B2 English requirement. Select ‘Not sure’ if your evidence still needs checking.",
      core: true,
    },
    {
      id: "founderRole",
      group: "Founder basics",
      prompt: "Will you have a key, active role in the day-to-day management and development of the business?",
      help: "The route is for founders who play a key role in the business rather than passive investors.",
      core: true,
    },
    {
      id: "founderAuthenticity",
      group: "Founder contribution and integrity",
      prompt: "Is the venture genuinely your own founder project, with a material contribution from you rather than a purchased, recycled or ready-made business idea?",
      help: "A senior review should confirm founder authenticity, ownership of the proposition and the evidence of your personal contribution.",
      core: false,
    },
    {
      id: "innovationCapability",
      group: "Founder contribution and integrity",
      prompt: "Can you personally lead or meaningfully control the core innovation, including any technical, product, research or specialist work that is central to the proposition?",
      help: "Outsourcing can support delivery, but the founder should be able to explain and evidence their own capability and control of the core innovation.",
      core: false,
    },
    {
      id: "evidenceIntegrity",
      group: "Founder contribution and integrity",
      prompt: "Can the important claims in your CV, business plan, customer evidence, finances, intellectual property and market evidence be supported by genuine records?",
      help: "Do not submit fabricated, purchased or misleading evidence. Unclear evidence should be discussed openly during senior review.",
      core: false,
    },
    {
      id: "newBusiness",
      group: "Business proposition",
      prompt: "For a new-business application, is this a business you intend to establish rather than simply joining an already trading business?",
      help: "New-business endorsement is intended for founders establishing the endorsed venture.",
      core: true,
    },
    {
      id: "innovative",
      group: "Business proposition",
      prompt: "Can you explain clearly what is original or meaningfully different about the proposition compared with existing market alternatives?",
      help: "Innovation should be evidenced, not just described with broad marketing language.",
      core: true,
    },
    {
      id: "viable",
      group: "Business proposition",
      prompt: "Do you have a credible plan showing that the proposition is realistic and that your skills, experience and resources can support delivery?",
      help: "Viability includes the business model and the founder’s capability to execute it.",
      core: true,
    },
    {
      id: "scalable",
      group: "Business proposition",
      prompt: "Can you evidence a credible growth plan, including job creation and expansion into wider UK and/or international markets?",
      help: "Scalability requires structured planning for growth rather than only a local self-employment concept.",
      core: true,
    },
    {
      id: "fundingSource",
      group: "Financial readiness",
      prompt: "If the new business needs funding, can you explain how much is required and provide a credible, legitimate source of those funds?",
      help: "There is no fixed investment amount, but an endorsing body may need to assess funding needs and source of funds.",
      core: false,
    },
    {
      id: "maintenance",
      group: "Financial readiness",
      prompt: "Where the maintenance requirement applies, can you evidence at least £1,270 held for the required 28-day period?",
      help: "If you believe the maintenance evidence requirement does not apply to you, choose ‘Not applicable’ and have this confirmed during review.",
      core: false,
      allowNotApplicable: true,
    },
    {
      id: "businessPlan",
      group: "Evidence readiness",
      prompt: "Do you already have a written business plan or structured concept document that can be reviewed?",
      help: "A draft is acceptable at this assessment stage.",
      core: false,
    },
    {
      id: "marketResearch",
      group: "Evidence readiness",
      prompt: "Do you have market research covering customers, competitors, demand and your market entry strategy?",
      help: "Evidence-based market research strengthens innovation, viability and scalability analysis.",
      core: false,
    },
    {
      id: "financialForecast",
      group: "Evidence readiness",
      prompt: "Do you have an initial financial forecast and assumptions for revenue, costs and funding needs?",
      help: "This is a readiness factor for business review, not a standalone visa requirement.",
      core: false,
    },
  ],
  documents: [
    { title: "Passport / identity document", type: "Visa application evidence" },
    { title: "English-language evidence", type: "Visa application evidence" },
    { title: "Maintenance-funds evidence, where required", type: "Visa application evidence" },
    { title: "TB test result, where required", type: "Visa application evidence" },
    { title: "Certified translations, where required", type: "Visa application evidence" },
    { title: "Founder CV / professional profile", type: "Endorsement preparation" },
    { title: "Founder-contribution / product / IP evidence, where available", type: "Endorsement preparation" },
    { title: "Business plan / concept paper", type: "Endorsement preparation" },
    { title: "Market and competitor research", type: "Endorsement preparation" },
    { title: "Financial forecast and funding assumptions", type: "Endorsement preparation" },
    { title: "Pitch deck / product evidence, if available", type: "Endorsement preparation" },
  ],
};

export const PROGRAMMES = [
  {
    slug: INNOVATOR_FOUNDER.slug,
    title: INNOVATOR_FOUNDER.title,
    eyebrow: INNOVATOR_FOUNDER.eyebrow,
    summary: INNOVATOR_FOUNDER.summary,
    status: "Assessment available",
  },
  {
    slug: "study-search",
    title: "International study search",
    eyebrow: "Education service",
    summary: "Compare published course information, shortlist options and start an application for staff review.",
    status: "Catalogue available",
    href: "/study",
  },
  {
    slug: "visa-consultation",
    title: "Visa and immigration consultation",
    eyebrow: "Consultation service",
    summary: "Begin with a structured enquiry for human review. This service does not provide an automated visa decision.",
    status: "Enquiry-led",
    href: "/visas",
  },
  {
    slug: "career-support",
    title: "Career and sponsorship support",
    eyebrow: "Career service",
    summary: "Explore jobs, sponsorship information and document preparation through the EuropEdu platform.",
    status: "Platform services",
    href: "/jobs",
  },
];

export function assessInnovatorFounder(answers) {
  const core = INNOVATOR_FOUNDER.questions.filter((q) => q.core);
  const unanswered = INNOVATOR_FOUNDER.questions.filter((q) => !answers[q.id]);
  const coreNo = core.filter((q) => answers[q.id] === "no");
  const coreUnsure = core.filter((q) => answers[q.id] === "unsure");
  const developmentNo = INNOVATOR_FOUNDER.questions.filter(
    (q) => !q.core && answers[q.id] === "no"
  );
  const developmentUnsure = INNOVATOR_FOUNDER.questions.filter(
    (q) => !q.core && answers[q.id] === "unsure"
  );

  if (unanswered.length) {
    return {
      key: "incomplete",
      title: "More information needed",
      tone: "amber",
      text: "Complete every assessment question before generating the initial review summary.",
      flags: unanswered.map((q) => q.prompt),
    };
  }

  if (coreNo.length) {
    return {
      key: "requirements",
      title: "Some current route factors are not met",
      tone: "rose",
      text: "One or more core Innovator Founder screening factors currently appear not to be met. This is not a visa refusal or legal determination; a senior review can confirm the position and discuss whether the issue can be addressed.",
      flags: coreNo.map((q) => q.prompt),
    };
  }

  if (coreUnsure.length || developmentNo.length || developmentUnsure.length) {
    return {
      key: "development",
      title: "Development recommended before senior review",
      tone: "amber",
      text: "The core proposition may warrant review, but there are unresolved route factors, founder-contribution questions or evidence gaps that should be developed before progressing toward endorsement preparation.",
      flags: [...coreUnsure, ...developmentNo, ...developmentUnsure].map((q) => q.prompt),
    };
  }

  return {
    key: "potential",
    title: "Potentially suitable for senior review",
    tone: "emerald",
    text: "Your answers indicate that the main initial screening factors are present. This result is only an automated readiness screen. An endorsing body and, where applicable, an appropriately authorised adviser must make their own assessments.",
    flags: [],
  };
}
